import { Text } from "ui/Typography"
import styles from "../index.module.css"
import moment from "moment"
import PropTypes from "prop-types"
import { getJournalIcon } from "../../../utils"
import IconWithBg from "ui/IconWithBg"
import { useCallback, useMemo, useState } from "react"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import { Button, Flex } from "antd"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffByDay } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import Journal from "../../../Modals/Journal"
import EventBlock from "../EventBlock"
import { useDevice } from "hooks/useDevice"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { getEventBorderColor, getEventColor, overlaps } from "pagesV2/pro/Calendar/utils"
import { useTranslation } from "react-i18next"

const HOUR_HEIGHT = 80 // px per hour — must match gridTemplateRows value

const ONE_MINUTE_MS = 60 * 1000

/**
 * Group events into overlap clusters (connected components).
 * Events in the same cluster directly or transitively overlap in time.
 * Each cluster is processed independently so `totalCols` reflects only
 * the cluster size — not the entire day — keeping isolated events full-width.
 */
const buildOverlapClusters = (events) => {
  const sorted = [...events].sort(
    (a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0)
  )
  const clusters = [] // each cluster: { events[], maxEnd }

  sorted.forEach((ev) => {
    const start = ev.startMoment?.valueOf() ?? 0
    const end   = ev.endMoment?.valueOf()   ?? start

    // Find all clusters whose time range overlaps this event
    const hit = clusters.filter((c) => c.maxEnd > start + ONE_MINUTE_MS)

    if (hit.length === 0) {
      clusters.push({ events: [ev], maxEnd: end })
    } else {
      // Merge all hit clusters together with this event
      const merged = hit.reduce(
        (acc, c) => {
          clusters.splice(clusters.indexOf(c), 1)
          acc.events.push(...c.events)
          acc.maxEnd = Math.max(acc.maxEnd, c.maxEnd)
          return acc
        },
        { events: [ev], maxEnd: end }
      )
      merged.maxEnd = Math.max(merged.maxEnd, end)
      clusters.push(merged)
    }
  })

  return clusters.map((c) => c.events)
}

/**
 * Assign a column index to each event within a single cluster.
 * A lane is reused only when the previous occupant ended > 1 min before the next starts.
 */
const assignColumnsInCluster = (clusterEvents) => {
  const sorted = [...clusterEvents].sort(
    (a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0)
  )
  const colEnds = []
  const assignments = []

  sorted.forEach((ev) => {
    const startVal = ev.startMoment?.valueOf() ?? 0
    const endVal   = ev.endMoment?.valueOf()   ?? startVal

    let colIndex = colEnds.findIndex((colEnd) => colEnd + ONE_MINUTE_MS <= startVal)
    if (colIndex === -1) {
      colIndex = colEnds.length
      colEnds.push(endVal)
    } else {
      colEnds[colIndex] = endVal
    }
    assignments.push({ ev, colIndex })
  })

  const totalCols = Math.max(colEnds.length, 1)
  return assignments.map(({ ev, colIndex }) => ({ ...ev, colIndex, totalCols }))
}

/**
 * Compute column layout for all events in a day.
 * Each overlap cluster gets its own independent totalCols so
 * an isolated event always renders full-width (totalCols = 1).
 */
const computeEventColumns = (events) =>
  buildOverlapClusters(events).flatMap(assignColumnsInCluster)

/**
 * Calculate absolute top (px) and height (px) for an event within the day column.
 * `startHour` is the first hour shown in the grid (hours[0]).
 */
const getEventPosition = (ev, startHour) => {
  const { startMoment, endMoment } = ev
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null

  const startMins = (startMoment.hour() - startHour) * 60 + startMoment.minute()
  const endMins   = (endMoment.hour()   - startHour) * 60 + endMoment.minute()
  const duration  = Math.max(endMins - startMins, 30) // minimum 30-min visual height

  return {
    top:    Math.max(0, startMins) * (HOUR_HEIGHT / 60),
    height: duration * (HOUR_HEIGHT / 60)
  }
}

const WeekGrid = ({ calendarDays, hours, events, agenda }) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [visibleCounts, setVisibleCounts] = useState({})
  const { t } = useTranslation()
  const MAX_VISIBLE = 4

  const getVisibleCount = useCallback((key) => visibleCounts[key] ?? MAX_VISIBLE, [visibleCounts])
  const showMore = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: (prev[key] ?? MAX_VISIBLE) + MAX_VISIBLE })), [])
  const showLess = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: MAX_VISIBLE })), [])
  const journalNoteDates = useCalendarJournalNoteDates()
  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const canViewJournal  = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal  = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)

  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD")
  const weekEndDate   = calendarDays?.at(-1)?.format("YYYY-MM-DD")
  const startHour     = hours[0] ?? 0
  const totalHeight   = hours.length * HOUR_HEIGHT

  const normalizedEventsByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = day.format("YYYY-MM-DD")
      acc[dayStr] = events
        .filter((ev) => moment(ev.date).isSame(day, "day"))
        .map((ev) => {
          const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
          const endMoment   = ev.end   ? moment(ev.end,   ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
          return {
            ...ev,
            startMoment,
            endMoment,
            startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
            endLabel:   endMoment?.isValid()   ? endMoment.format("h:mm A")   : ev.end
          }
        })
      return acc
    }, {})
  }, [calendarDays, events])

  // Precompute event columns per day so we can derive per-day column widths
  const eventsByDayWithCols = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = day.format("YYYY-MM-DD")
      const valid  = (normalizedEventsByDay[dayStr] || []).filter(
        (ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid()
      )
      acc[dayStr] = computeEventColumns(valid)
      return acc
    }, {})
  }, [calendarDays, normalizedEventsByDay])

  const showAsAgenda = agenda

  // Each day column min-width = max lanes for that day × MIN_LANE_WIDTH
  const MIN_LANE_WIDTH = 100 // px per event lane
  const gridTemplateColumns = useMemo(() => {
    const dayCols = calendarDays.map((day) => {
      const dayStr  = day.format("YYYY-MM-DD")
      const evsCols = eventsByDayWithCols[dayStr] || []
      const lanes   = evsCols.length > 0 ? Math.max(...evsCols.map((e) => e.totalCols)) : 1
      const minW    = Math.max(120, lanes * MIN_LANE_WIDTH)
      return `minmax(${minW}px, 1fr)`
    })
    return agenda ? `80px ${dayCols.join(" ")}` : dayCols.join(" ")
  }, [calendarDays, eventsByDayWithCols, agenda])

  const gridMinWidth = useMemo(() => {
    const dayWidths = calendarDays.reduce((sum, day) => {
      const dayStr  = day.format("YYYY-MM-DD")
      const evsCols = eventsByDayWithCols[dayStr] || []
      const lanes   = evsCols.length > 0 ? Math.max(...evsCols.map((e) => e.totalCols)) : 1
      return sum + Math.max(120, lanes * MIN_LANE_WIDTH)
    }, 0)
    return agenda ? 80 + dayWidths : dayWidths
  }, [calendarDays, eventsByDayWithCols, agenda])

  const timeOffByDay = useTimeOffByDay(calendarDays)
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)

  const handleJournalModalOpen = (date) => { setIsJournalModalOpen(true); setSelectedDate(date) }
  const handleCancel = () => setIsJournalModalOpen(false)
  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  // Non-agenda (no time column): render simple stacked card layout
  if (!showAsAgenda) {
    return (
      <>
        <div className={styles.nonAgendaWrapper}>
          <table className={styles.nonAgendaTable}>
            <thead>
              <tr>
                {calendarDays.map((day) => {
                  const dayStr = day.format("YYYY-MM-DD")
                  const hasJournalNote = journalNoteDates?.includes(dayStr)
                  const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal
                  const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
                  return (
                    <th key={dayStr} className={styles.nonAgendaHeaderCell}>
                      <Flex vertical align="center" gap={2}>
                        <Text fw="500">{dayLabel}</Text>
                        <Text fw="500">{day.format("D")}</Text>
                      </Flex>
                      {!isCompact && hasJournalPermission && (
                        <Flex justify="center" className="mt-xxs">
                          <IconWithBg
                            disabled={!active}
                            icon={getJournalIcon(journalNoteDates, dayStr)}
                            onClick={active ? () => handleJournalModalOpen(dayStr) : undefined}
                          />
                        </Flex>
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {calendarDays.map((day) => {
                  const dayStr = day.format("YYYY-MM-DD")
                  const dayEvents = normalizedEventsByDay[dayStr] || []
                  const visibleCount = getVisibleCount(dayStr)
                  const visibleEvents = dayEvents.slice(0, visibleCount)
                  const allVisible = visibleCount >= dayEvents.length
                  const hasMore = !allVisible && dayEvents.length > MAX_VISIBLE
                  const canShowLess = allVisible && dayEvents.length > MAX_VISIBLE
                  return (
                    <td key={dayStr} className={styles.nonAgendaBodyCell}>
                      {visibleEvents.map((ev) => (
                        <EventBlock
                          key={ev.id ?? (ev.title + ev.start)}
                          event={ev}
                          eventColor={getEventColor(ev.payment_status)}
                          eventBorderColor={getEventBorderColor(ev.payment_status)}
                          setIsBookingModalOpen={setIsBookingModalOpen}
                          setSelectedBookingId={setSelectedBookingId}
                        />
                      ))}
                      {hasMore && (
                        <Button type="link" size="small" onClick={() => showMore(dayStr)}>
                          {t("calendar.view_more")}
                        </Button>
                      )}
                      {canShowLess && (
                        <Button type="link" size="small" onClick={() => showLess(dayStr)}>
                          Read less
                        </Button>
                      )}
                    </td>
                  )
                })}
              </tr>
              {calendarDays.every((day) => (normalizedEventsByDay[day.format("YYYY-MM-DD")] || []).length === 0) &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={`placeholder-${i}`}>
                    {calendarDays.map((day) => (
                      <td key={day.format("YYYY-MM-DD")} className={styles.nonAgendaBodyCell} />
                    ))}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {isJournalModalOpen && (
          <Journal open={isJournalModalOpen} setOpen={setIsJournalModalOpen} onCancel={handleCancel} selectedDate={selectedDate} startDate={weekStartDate} endDate={weekEndDate} />
        )}
        {isBookingModalOpen && (
          <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} onCancelSuccess={handleBookingCancelSuccess} />
        )}
      </>
    )
  }

  return (
    <>
    <div className={styles.timeGridRoot}>
    <div
      className={`${styles.timeGridTable} ${styles.weekGrid}`}
      style={{
        gridTemplateRows: `80px repeat(${hours.length}, ${HOUR_HEIGHT}px)`,
        gridTemplateColumns,
        minWidth: `${gridMinWidth}px`
      }}
    >
      {/* ── Row 1: Header ── */}
      {showAsAgenda && (
        <div className={styles.timeGridHeaderCell} style={{ gridRow: 1, gridColumn: 1 }} />
      )}
      {calendarDays.map((day, dayIdx) => {
        const dayStr = day.format("YYYY-MM-DD")
        const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
        const hasJournalNote = journalNoteDates?.includes(dayStr)
        const hasJournalPermission = hasJournalNote
          ? canViewJournal || canEditJournal
          : canCreateJournal
        return (
          <Flex
            key={dayStr}
            gap={5}
            className={`py-s px-xs ${styles.timeGridHeaderCell}`}
            align="center"
            justify="center"
            style={{ gridRow: 1, gridColumn: showAsAgenda ? dayIdx + 2 : dayIdx + 1 }}
          >
            <Flex
              vertical
              justify={isCompact ? "space-between" : "center"}
              className={`${isCompact ? "h-full" : ""} ml-auto`}
            >
              <Text fw="500">{dayLabel}</Text>
              <Text fw="500">{day.format("D")}</Text>
            </Flex>
            {!isCompact && hasJournalPermission && (
              <Flex className="ml-auto" align="center">
                <IconWithBg
                  disabled={!active}
                  icon={getJournalIcon(journalNoteDates, dayStr)}
                  onClick={active ? () => handleJournalModalOpen(dayStr) : undefined}
                />
              </Flex>
            )}
          </Flex>
        )
      })}

      {/* ── Time labels (one per hour row, column 1 when agenda) ── */}
      {showAsAgenda && hours.map((hour, hIdx) => (
        <div
          key={`label-${hour}`}
          className={styles.timeLabelCell}
          style={{ gridRow: hIdx + 2, gridColumn: 1 }}
        >
          <Text fw="500" className="py-m">
            {moment(hour, "H").format("h A")}
          </Text>
        </div>
      ))}

      {/* ── Day columns: each spans ALL hour rows ── */}
      {calendarDays.map((day, dayIdx) => {
        const dayStr     = day.format("YYYY-MM-DD")
        const eventsWithCols = eventsByDayWithCols[dayStr] || []
        const timeOffForDay  = timeOffByDay[dayStr]

        return (
          <div
            key={`col-${dayStr}`}
            style={{
              gridRow: `2 / span ${hours.length}`,
              gridColumn: showAsAgenda ? dayIdx + 2 : dayIdx + 1,
              position: "relative",
              height: totalHeight,
              boxSizing: "border-box",
              borderRight: "1px solid var(--color-surface3)"
            }}
          >
            {/* Hour divider lines (background grid) */}
            {hours.map((hour, hIdx) => (
              <div
                key={`divider-${hour}`}
                style={{
                  position: "absolute",
                  top: hIdx * HOUR_HEIGHT,
                  left: 0,
                  right: 0,
                  height: HOUR_HEIGHT,
                  borderBottom: "1px solid var(--color-surface3)",
                  pointerEvents: "none",
                  boxSizing: "border-box"
                }}
              />
            ))}

            {/* Time-off overlay — all day */}
            {timeOffForDay?.allDay && (
              <div
                className={styles.timeOffOverlay}
                style={{ position: "absolute", top: 0, height: "100%", left: 0, right: 0, zIndex: 1 }}
              />
            )}

            {/* Time-off overlay — partial ranges */}
            {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).map((toff) => {
              const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
              const botPx = Math.max(0, (toff.end.hour()   - startHour) * 60 + toff.end.minute())   * (HOUR_HEIGHT / 60)
              return (
                <div
                  key={`${dayStr}-${toff.start.format("HH:mm")}`}
                  className={styles.timeOffOverlay}
                  style={{ position: "absolute", top: topPx, height: Math.max(0, botPx - topPx), left: 0, right: 0, zIndex: 1 }}
                />
              )
            })}

            {/* Event blocks — absolutely positioned by time, side-by-side when overlapping */}
            {eventsWithCols.map((ev) => {
              const pos = getEventPosition(ev, startHour)
              if (!pos) return null

              const colW    = 98 / ev.totalCols
              const leftPct = ev.colIndex * colW + 0.5
              const widthPct = colW - 0.5

              const conflict =
                timeOffForDay?.allDay ||
                (timeOffForDay?.ranges || []).some((r) =>
                  overlaps(ev.startMoment, ev.endMoment, r.start, r.end)
                )

              return (
                <div
                  key={ev.id ?? (ev.title + ev.start)}
                  style={{
                    position: "absolute",
                    top: pos.top,
                    height: pos.height,
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    zIndex: 2,
                    boxSizing: "border-box"
                  }}
                >
                  <EventBlock
                    event={ev}
                    eventColor={getEventColor(ev.payment_status)}
                    eventBorderColor={getEventBorderColor(ev.payment_status)}
                    conflict={conflict}
                    setIsBookingModalOpen={setIsBookingModalOpen}
                    setSelectedBookingId={setSelectedBookingId}
                    fullHeight
                  />
                </div>
              )
            })}
          </div>
        )
      })}

    </div>
    </div>
    {isJournalModalOpen && (
      <Journal
        open={isJournalModalOpen}
        setOpen={setIsJournalModalOpen}
        onCancel={handleCancel}
        selectedDate={selectedDate}
        startDate={weekStartDate}
        endDate={weekEndDate}
      />
    )}
    {isBookingModalOpen && (
      <BookingDetailsModal
        open={isBookingModalOpen}
        handleOnCancel={handleBookingCancel}
        bookingUuid={selectedBookingId}
        onCancelSuccess={handleBookingCancelSuccess}
      />
    )}
    </>
  )
}

WeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  agenda: PropTypes.bool
}

export default WeekGrid
